import type { Chart as ChartJs, TooltipModel } from "chart.js";

import type { Chart, Series } from "~/lib/chart-spec";
import {
  type ChartKind,
  type ChartTheme,
  formatValue,
  seriesColor,
} from "~/lib/charts";

const element = <K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};

const swatch = (
  series: Series,
  index: number,
  theme: ChartTheme,
): HTMLElement => {
  const color = theme.colors[seriesColor(series, index)];
  if (series.kind === "bar") {
    const node = element(
      "span",
      "inline-block size-2.5 shrink-0 rounded-sm align-middle",
    );
    node.style.backgroundColor = color;
    return node;
  }
  const node = element(
    "span",
    "inline-block w-3 shrink-0 border-t-2 align-middle",
  );
  node.style.borderColor = color;
  node.style.borderTopStyle = series.dash === "solid" ? "solid" : series.dash;
  return node;
};

export const renderLegend = (
  container: HTMLElement,
  spec: Chart,
  theme: ChartTheme,
): void => {
  container.replaceChildren(
    ...spec.series.map((series, index) => {
      const item = series.url
        ? Object.assign(element("a", ""), { href: series.url })
        : element("span", "");
      item.classList.add(
        "flex",
        "items-center",
        "gap-1.5",
        "text-xs",
        "text-gray-900",
      );
      item.append(swatch(series, index, theme), series.label);
      return item;
    }),
  );
};

export const renderTooltip = (
  {
    chart,
    tooltip,
  }: { chart: ChartJs<ChartKind>; tooltip: TooltipModel<ChartKind> },
  spec: Chart,
  theme: ChartTheme,
  totalLabel: string,
): void => {
  const container = chart.canvas.parentElement;
  if (container === null) return;
  let box = container.querySelector<HTMLElement>(
    ":scope > [data-chart-tooltip]",
  );
  if (box === null) {
    box = element(
      "div",
      "pointer-events-none absolute z-20 rounded-md bg-white p-2 text-xs text-gray-900 shadow-md ring-1 ring-stroke whitespace-nowrap",
    );
    box.dataset.chartTooltip = "";
    container.append(box);
  }
  if (tooltip.opacity === 0 || tooltip.dataPoints.length === 0) {
    box.hidden = true;
    return;
  }

  const format = spec.y.format;
  const rows = element("table", "border-separate border-spacing-x-2 -mx-2");
  for (const point of tooltip.dataPoints) {
    const series = spec.series[point.datasetIndex];
    if (series === undefined) continue;
    const row = rows.insertRow();
    if (spec.series.length > 1) {
      row.insertCell().append(swatch(series, point.datasetIndex, theme));
      row.insertCell().textContent = series.label;
    }
    const value = row.insertCell();
    value.className = "text-right tabular-nums";
    value.textContent = formatValue(point.raw as number, format, "one");
  }
  if (spec.show_total && spec.series.length > 1) {
    const total = tooltip.dataPoints
      .filter((point) => spec.series[point.datasetIndex]?.kind === "bar")
      .reduce((sum, point) => sum + (point.raw as number), 0);
    const row = rows.insertRow();
    row.className = "font-semibold";
    row.insertCell();
    row.insertCell().textContent = totalLabel;
    const value = row.insertCell();
    value.className = "text-right tabular-nums";
    value.textContent = formatValue(total, format, "one");
  }
  const index = tooltip.dataPoints[0]?.dataIndex;
  const notes = index === undefined ? [] : (spec.notes?.[index] ?? []);
  box.replaceChildren(
    element("div", "mb-1 font-semibold", tooltip.title.join(" ")),
    rows,
    ...notes.map((note) => element("div", "mt-1 text-gray-500", note)),
  );

  box.hidden = false;
  const gap = 12;
  const fitsRight =
    tooltip.caretX + gap + box.offsetWidth <= container.clientWidth;
  box.style.left = `${fitsRight ? tooltip.caretX + gap : Math.max(0, tooltip.caretX - gap - box.offsetWidth)}px`;
  box.style.top = `${Math.max(0, tooltip.caretY - box.offsetHeight / 2)}px`;
};
